// pbValuation.js
// Computes Fair Price using the P/B v5 "BasePB × Multipliers + Overlay" framework.
//
// Expected API payload shape:
// { valuationParams: { ... } }
//
// User can pass either:
// - driversOverride: absolute values for the 4 tweakable drivers, OR
// - driversDelta: deltas around baseDrivers (optional)

export const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
export const clamp = (x, min, max) => Math.min(max, Math.max(min, x));

/**
 * Convert "valuationParams wrapper" returned by API into a usable params object.
 * Allows your UI to pass the API payload directly.
 */
export function unwrapPBParams(apiResponseOrParams) {
  if (!apiResponseOrParams) return null;
  if (apiResponseOrParams.valuationParams) return apiResponseOrParams.valuationParams;
  return apiResponseOrParams; // already unwrapped
}

/**
 * Compute P/B valuation with controlled sensitivities.
 *
 * Core model:
 * basePB_adj = basePB * (1 + basePB_per1ppROE * (ROE - baseROE))
 * growthMult_adj = baseGrowth * (1 + growthMult_per1ppAUM * (AUMCAGR - baseAUMCAGR))
 * assetMult_adj  = baseAsset  * (1 - assetMult_per1ppGNPA * (GNPA - baseGNPA))
 * capMult_adj    = baseCap    * (1 - capFundMult_per1xDE  * (DE - baseDE))
 *
 * justifiedPB = basePB_adj * growthMult_adj * assetMult_adj * capMult_adj + govOverlay
 * justifiedPB is clamped to maxPB
 *
 * fairPrice = justifiedPB * latestBVPS
 */
export function computePBValuation({
  apiPayload,          // pass the whole API response OR omit and pass params directly
  params,              // alternative to apiPayload; can pass valuationParams directly
  driversOverride,     // { roeAvgPct, aumCagrPct, gnpaPct, debtToEquity }
  driversDelta,        // optional: deltas from base drivers
  clampDrivers = true, // clamp inputs within driverRanges
} = {}) {
  console.log("computePBValuation called with apipayload:", { apiPayload });
  console.log("computePBValuation called with params:", { params });
  console.log("computePBValuation called with driversOverride:", { driversOverride });
  console.log("computePBValuation called with driversDelta:", { driversDelta });
  console.log("computePBValuation called with clampDrivers:", { clampDrivers });



  const p = unwrapPBParams(apiPayload || params);
  if (!p) throw new Error("Missing valuation params. Pass apiPayload or params.");

  // Handle nested structure - valuationCore contains the core parameters
  const core = p.valuationCore || p;

  const {
    stockName = p.stockName,
  } = p;

  const {
    latestBVPS,
    currentPrice = 0,
    basePB,
    maxPB,
    baseMultipliers,
    govOverlay = 0,
    baseDrivers,
    driverRanges,
    sensitivities,
    multiplierClamps,
  } = core;

  // --- 1) Determine effective drivers (base +/- delta and/or override) ---
  const drivers = { ...baseDrivers };

  if (driversDelta) {
    if (typeof driversDelta.roeAvgPct === "number") drivers.roeAvgPct += driversDelta.roeAvgPct;
    if (typeof driversDelta.aumCagrPct === "number") drivers.aumCagrPct += driversDelta.aumCagrPct;
    if (typeof driversDelta.gnpaPct === "number") drivers.gnpaPct += driversDelta.gnpaPct;
    if (typeof driversDelta.debtToEquity === "number") drivers.debtToEquity += driversDelta.debtToEquity;
  }

  if (driversOverride) {
    if (typeof driversOverride.roeAvgPct === "number") drivers.roeAvgPct = driversOverride.roeAvgPct;
    if (typeof driversOverride.aumCagrPct === "number") drivers.aumCagrPct = driversOverride.aumCagrPct;
    if (typeof driversOverride.gnpaPct === "number") drivers.gnpaPct = driversOverride.gnpaPct;
    if (typeof driversOverride.debtToEquity === "number") drivers.debtToEquity = driversOverride.debtToEquity;
  }

  // --- 2) Clamp drivers to tight UI ranges (bull/bear around base) ---
  if (clampDrivers && driverRanges) {
    if (driverRanges.roeAvgPct) {
      drivers.roeAvgPct = clamp(drivers.roeAvgPct, driverRanges.roeAvgPct.min, driverRanges.roeAvgPct.max);
    }
    if (driverRanges.aumCagrPct) {
      drivers.aumCagrPct = clamp(drivers.aumCagrPct, driverRanges.aumCagrPct.min, driverRanges.aumCagrPct.max);
    }
    if (driverRanges.gnpaPct) {
      drivers.gnpaPct = clamp(drivers.gnpaPct, driverRanges.gnpaPct.min, driverRanges.gnpaPct.max);
    }
    if (driverRanges.debtToEquity) {
      drivers.debtToEquity = clamp(drivers.debtToEquity, driverRanges.debtToEquity.min, driverRanges.debtToEquity.max);
    }
  }

  // --- 3) Compute deltas vs base ---
  console.log("Effective drivers after applying deltas/overrides and clamping:", { drivers });
  const dROE = drivers.roeAvgPct - baseDrivers.roeAvgPct;            // + is good
  const dAUM = drivers.aumCagrPct - baseDrivers.aumCagrPct;          // + is good
  const dGNPA = drivers.gnpaPct - baseDrivers.gnpaPct;               // + is bad
  const dDE = drivers.debtToEquity - baseDrivers.debtToEquity;       // + is bad

  // --- 4) Apply sensitivities (small controlled impacts) ---
  const s = sensitivities || {};

  const basePBAdj = basePB * (1 + (s.basePB_per1ppROE || 0) * dROE);

  let growthMultAdj = baseMultipliers.growth * (1 + (s.growthMult_per1ppAUM || 0) * dAUM);
  let assetMultAdj = baseMultipliers.assetQuality * (1 - (s.assetMult_per1ppGNPA || 0) * dGNPA);
  let capFundMultAdj = baseMultipliers.capitalFunding * (1 - (s.capFundMult_per1xDE || 0) * dDE);

  // --- 5) Clamp multipliers so tweaks don’t create extreme valuations ---
  const mc = multiplierClamps || {};
  if (mc.growth) growthMultAdj = clamp(growthMultAdj, mc.growth.min, mc.growth.max);
  if (mc.assetQuality) assetMultAdj = clamp(assetMultAdj, mc.assetQuality.min, mc.assetQuality.max);
  if (mc.capitalFunding) capFundMultAdj = clamp(capFundMultAdj, mc.capitalFunding.min, mc.capitalFunding.max);

  // --- 6) Final Justified P/B and Fair Price ---
  let justifiedPB = basePBAdj * growthMultAdj * assetMultAdj * capFundMultAdj + govOverlay;
  if (typeof maxPB === "number") justifiedPB = Math.min(justifiedPB, maxPB);

  const fairPrice = justifiedPB * latestBVPS;
  const marginOfSafety =
    currentPrice && currentPrice > 0 ? (fairPrice - currentPrice) / currentPrice : null;

  return {
    stockName,
    // Inputs after clamp
    drivers: {
      roeAvgPct: round2(drivers.roeAvgPct),
      aumCagrPct: round2(drivers.aumCagrPct),
      gnpaPct: round2(drivers.gnpaPct),
      debtToEquity: round2(drivers.debtToEquity),
    },
    deltas: {
      dROE: round2(dROE),
      dAUM: round2(dAUM),
      dGNPA: round2(dGNPA),
      dDE: round2(dDE),
    },
    // Computed components (useful for UI “explainability”)
    components: {
      basePB: round2(basePB),
      basePBAdj: round2(basePBAdj),
      multipliersBase: {
        growth: round2(baseMultipliers.growth),
        assetQuality: round2(baseMultipliers.assetQuality),
        capitalFunding: round2(baseMultipliers.capitalFunding),
      },
      multipliersAdj: {
        growth: round2(growthMultAdj),
        assetQuality: round2(assetMultAdj),
        capitalFunding: round2(capFundMultAdj),
      },
      govOverlay: round2(govOverlay),
      maxPB: maxPB,
      latestBVPS: round2(latestBVPS),
    },
    justifiedPB: round2(justifiedPB),
    fairPrice: round2(fairPrice),
    currentPrice: round2(currentPrice),
    marginOfSafety: marginOfSafety === null ? null : round2(marginOfSafety),
  };
}

/**
 * P/B Valuation Engine for NBFCs
 * Computes fair price using scorecard-based P/B methodology
 */

export function computePBValuationNBFC({ apiPayload, driversOverride = {} }) {
  const core = apiPayload.valuationParams.valuationCore;
  const scorecard = apiPayload.valuationParams.scorecardSnapshot;

  // Extract base parameters
  const baseDrivers = core.baseDrivers;
  const sensitivities = core.sensitivities;
  const multiplierClamps = core.multiplierClamps;
  const driverRanges = core.driverRanges;

  // Apply user overrides or use base values
  const roeAvgPct = driversOverride.roeAvgPct ?? baseDrivers.roeAvgPct;
  const aumCagrPct = driversOverride.aumCagrPct ?? baseDrivers.aumCagrPct;
  const gnpaPct = driversOverride.gnpaPct ?? baseDrivers.gnpaPct;
  const debtToEquity = driversOverride.debtToEquity ?? baseDrivers.debtToEquity;

  // Calculate adjusted base P/B based on ROE
  const roeDelta = roeAvgPct - baseDrivers.roeAvgPct;
  const basePBAdj = core.basePB + (roeDelta * sensitivities.basePB_per1ppROE);

  // Calculate adjusted Growth Multiplier based on AUM CAGR
  const aumDelta = aumCagrPct - baseDrivers.aumCagrPct;
  const growthMultBase = core.baseMultipliers.growth;
  const growthMultAdj = Math.max(
    multiplierClamps.growth.min,
    Math.min(
      multiplierClamps.growth.max,
      growthMultBase + (aumDelta * sensitivities.growthMult_per1ppAUM)
    )
  );

  // Calculate adjusted Asset Quality Multiplier based on GNPA
  const gnpaDelta = gnpaPct - baseDrivers.gnpaPct;
  const assetQMultBase = core.baseMultipliers.assetQuality;
  const assetQMultAdj = Math.max(
    multiplierClamps.assetQuality.min,
    Math.min(
      multiplierClamps.assetQuality.max,
      assetQMultBase - (gnpaDelta * sensitivities.assetMult_per1ppGNPA)
    )
  );

  // Calculate adjusted Capital & Funding Multiplier based on D/E
  const deDelta = debtToEquity - baseDrivers.debtToEquity;
  const capFundMultBase = core.baseMultipliers.capitalFunding;
  const capFundMultAdj = Math.max(
    multiplierClamps.capitalFunding.min,
    Math.min(
      multiplierClamps.capitalFunding.max,
      capFundMultBase - (deDelta * sensitivities.capFundMult_per1xDE)
    )
  );

  // Calculate Justified P/B
  const justifiedPB = Math.min(
    basePBAdj * growthMultAdj * assetQMultAdj * capFundMultAdj + core.govOverlay,
    core.maxPB
  );

  // Calculate Fair Price
  const fairPrice = justifiedPB * core.latestBVPS;

  // Calculate Implied P/E (if available)
  const impliedPE = scorecard?.outputsFromXls?.impliedPE || null;

  // Calculate Margin of Safety if current price is available
  let marginOfSafety = null;
  if (core.currentPrice && core.currentPrice > 0) {
    marginOfSafety = ((fairPrice - core.currentPrice) / core.currentPrice);
  }

  return {
    fairPrice: fairPrice,
    justifiedPB: justifiedPB,
    impliedPE: impliedPE,
    marginOfSafety: marginOfSafety,
    breakdown: {
      latestBVPS: core.latestBVPS,
      basePBAdj: basePBAdj,
      growthMultiplier: growthMultAdj,
      assetQualityMultiplier: assetQMultAdj,
      capitalFundingMultiplier: capFundMultAdj,
      govOverlay: core.govOverlay,
      maxPB: core.maxPB,
      beforeCap: basePBAdj * growthMultAdj * assetQMultAdj * capFundMultAdj + core.govOverlay
    },
    inputs: {
      roeAvgPct,
      aumCagrPct,
      gnpaPct,
      debtToEquity
    }
  };
}
